import { ConfirmDialogProvider, useTheme } from '@hgc/ui';
import { useEffect, type JSX } from 'react';

import { useAppStore } from '../runtime/AppStoreContext';
import { useSectionRoute } from '../runtime/hooks';
import { AppToolbar } from './AppToolbar';
import { normalizeRoute, type SectionId } from './route';
import { AnalyzeSection } from './sections/AnalyzeSection';
import { FlySection } from './sections/FlySection';
import { PlanSection } from './sections/PlanSection';
import { SetupSection } from './sections/SetupSection';

/**
 * 应用外壳。
 *
 * 结构是 QGC 那一套：**顶部一条工具栏 + 一整块内容区**。
 * 工具栏承担"我在操作什么"（分区、链路、机体、外观），内容区完全交给当前分区。
 *
 * 两个分区族对内容区的需求不同，所以 `main` 自己不带内边距：
 *   - **飞行**是操作台，自己管满高与内部滚动（地图/仪表要贴边）；
 *   - **计划 / 设置 / 分析**是文档流，由 `SectionFrame` 统一给内边距与滚动。
 * 让 `main` 带内边距的话，飞行分区就得用负边距去抵消它 —— 那种写法一旦有人改了
 * `main` 的 `p-3`，飞行页会悄悄错位，且看不出是谁改的。
 *
 * 硬约束（写在这里是因为它最容易被无意破坏）：
 * `@hgc/ui` 的浮层系统用 `position: fixed` 且**不用 react-dom 的 portal**，
 * 所以应用根节点这条链上**不能**有 `transform` / `filter` / `will-change` 祖先
 * （它们会把 `fixed` 的包含块改成那个祖先），也不能被 `overflow: hidden` 裁剪 ——
 * 否则 `realtime` 作用域的浮层会被裁掉或定位错乱，而这个症状看起来与浮层代码毫无关系。
 * 约束固化在 `src/styles/index.css`（未分层规则，优先级高于任何工具类）与 `index.html`。
 */
export function App(): JSX.Element {
  const store = useAppStore();
  const section = useSectionRoute();

  /*
   * 两种外观状态：`data-resolved-theme` 是给**读不到我们 store 的渲染器**用的
   * （地图样式、图表配色将来都要按深浅色切换，它们是第三方代码，只能从 DOM 上读）。
   * 这也是第二处 `useTheme()` 消费点 —— 它必须与工具栏的按钮看到同一份状态。
   */
  const { resolved } = useTheme();

  // 启动默认链路（内置模拟机）。放在 effect 里而不是模块顶层：
  // 模块顶层建定时器意味着"import 一下就多出一条后台链路"，测试里尤其致命。
  useEffect(() => {
    store.start();
  }, [store]);

  // 把地址栏里的分区归一化（空 hash / 手输错的 hash 都落到"飞行"并写回）。
  // 不写回的话，后退键会先回到那个无效地址、再被弹回飞行，表现为"后退失灵"。
  useEffect(() => {
    normalizeRoute();
  }, []);

  return (
    /*
     * `ConfirmDialogProvider` 挂在应用最外层，且只挂这一处。
     *
     * 它承载**全应用唯一的**确认气泡与功能弹窗宿主：`useConfirm()` / `useDialog()`
     * 都是从它读上下文的。散在各个分区里各挂一个，就会出现"同一个确认框在不同分区
     * 长得不一样、深浅色/z 序不一致"的问题 —— 统一弹窗系统的价值恰恰在于只有一处。
     *
     * 挂在 `App` 而不是 `main.tsx`：这样单测里只要渲染 `<App />` 就直接可用，
     * 不必每个测试各自记得包一层。
     */
    <ConfirmDialogProvider>
      <div
        data-resolved-theme={resolved}
        className="flex h-full min-h-0 flex-col bg-surface-0 text-fg"
      >
        <AppToolbar active={section} />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <SectionHost section={section} />
        </main>
      </div>
    </ConfirmDialogProvider>
  );
}

/**
 * 分区内容挂载点。
 *
 * 用 `switch` 而不是路由表：四个分区没有参数、没有嵌套、没有守卫，
 * 一张表只会多一层间接寻址。真需要路由参数时（例如"打开某个参数"），
 * 再换成表也不迟。
 */
function SectionHost({ section }: { readonly section: SectionId }): JSX.Element {
  switch (section) {
    case 'fly':
      return <FlySection />;
    case 'plan':
      return (
        <SectionFrame>
          <PlanSection />
        </SectionFrame>
      );
    case 'setup':
      return (
        <SectionFrame>
          <SetupSection />
        </SectionFrame>
      );
    case 'analyze':
      return (
        <SectionFrame>
          <AnalyzeSection />
        </SectionFrame>
      );
  }
}

/**
 * 文档流分区的统一外壳：内边距 + 自己滚动 + 限宽。
 *
 * `max-w` 是给宽屏的：设置页的表单在 2560px 宽屏上拉满会让输入框长到荒谬，
 * 也让"标签 → 输入框"的视线距离变成一次转头。
 */
function SectionFrame({ children }: { readonly children: JSX.Element }): JSX.Element {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-3 p-3">{children}</div>
    </div>
  );
}

